import { InitialLoading } from '@/components/loading/initial';
import { useCreateFeedLinkData } from '@/query';
import { useAppStore } from '@/shared/stores/app';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { OfflineFeedLinkDexieService } from './sync-feed-link/offline-feed-link-service';

const feedLinkSyncService = new OfflineFeedLinkDexieService();

export function AppConfig({ children }: { children: React.ReactNode }) {
  const { setApp } = useAppStore();

  const [_, setFeedLinkRefetch] = useQueryState('link:refetch', parseAsBoolean.withDefault(false));

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const createFeedLinkData = useCreateFeedLinkData();

  // Timer para detectar 5s de ociosidade após processar dados
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const IDLE_DELAY_MS = 5000;

  useEffect(() => {
    feedLinkSyncService.setProcessor(async (body) => {
      await createFeedLinkData.mutateAsync({ data: body });
    });

    // Após um ciclo de processamento, reinicia o timer de ociosidade;
    // se ficar 5s sem novos processamentos, dispara o refetch.
    feedLinkSyncService.setOnProcessed(() => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        setFeedLinkRefetch(true);
        idleTimerRef.current = null;
      }, IDLE_DELAY_MS);
    });

    feedLinkSyncService.sync();

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [createFeedLinkData, setFeedLinkRefetch]);

  useEffect(() => {
    window.electronAPI.appVersion().then((value) => {
      setApp(value);
      setIsLoading(false);
    });

    window.electronAPI.feedLink.onSuccess(async (data) => {
      const { id, ...payload } = data;
      try {
        await feedLinkSyncService.addSuccess(payload as any);
        window.electronAPI.feedLink.ackSuccess(id, true);
      } catch (err: any) {
        const message = err?.message ?? String(err);
        console.error('[feed-link] falha ao salvar success no IndexedDB, solicitando retry:', err);
        window.electronAPI.feedLink.ackSuccess(id, false, message);
      }
    });

    window.electronAPI.feedLink.onError(async (data) => {
      await feedLinkSyncService.addError(data as any);
    });

    return () => {
      window.electronAPI.feedLink.removeAllListeners('feed-link-success');
      window.electronAPI.feedLink.removeAllListeners('feed-link-error');
    };
  }, []);

  if (isLoading) {
    return <InitialLoading text="Carregando o feed manager..." />;
  }

  return children;
}
